import React, { useState } from 'react';
import { Listbox } from '@headlessui/react';
import {
	CheckIcon,
	ChevronUpDownIcon,
	PlusIcon,
	TrashIcon,
	CalendarIcon,
	ClockIcon,
	DocumentTextIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { getModuleCategoriesByModuleKey } from '../../api/modules';
import { getApprovedSops } from '../../api/sops';
import useAsync from '../../hooks/useAsync';
import { classNames } from '../../components/helpers/functions';
import Button from '../../components/Button';
import { DatePicker } from '../../components/nextgen';
import moment from 'moment';

const FREQUENCY_OPTIONS = [
	{ key: 'daily', name: 'Daily' },
	{ key: 'weekly', name: 'Weekly' },
	{ key: 'biweekly', name: 'Bi-weekly' },
	{ key: 'monthly', name: 'Monthly' },
	{ key: 'quarterly', name: 'Quarterly' },
	{ key: 'biyearly', name: 'Bi-yearly' },
	{ key: 'yearly', name: 'Yearly' },
	{ key: 'custom', name: 'Custom Dates' },
];

const DEFAULT_SCHEDULE_FORM = {
	name: '',
	description: '',
	categoryId: null,
	occurrences: 'yearly',
	customDates: [],
	startDate: '',
	generateDaysBefore: 30,
	sopId: null,
	serviceReportRequired: true,
};

const ContractPMSchedules = ({
	pmSchedulesForm,
	setPMSchedulesForm,
	updatedForm,
}) => {
	const [isAddingSchedule, setIsAddingSchedule] = useState(false);
	const [scheduleForm, setScheduleForm] = useState(DEFAULT_SCHEDULE_FORM);

	// Fetch categories and SOPs
	const { value: categories } = useAsync(getModuleCategoriesByModuleKey, {
		defaultPayload: { key: 'default:pm_schedule' },
	});
	const { value: sops } = useAsync(getApprovedSops, {
		defaultPayload: {},
	});

	// Get contract date constraints
	const coverageStart = updatedForm?.['coverage-start']?.value;
	const coverageEnd = updatedForm?.['coverage-end']?.value;

	const handleAddSchedule = () => {
		if (!scheduleForm.name.trim()) return;

		console.log('handleAddSchedule - scheduleForm:', scheduleForm);
		console.log('handleAddSchedule - scheduleForm.occurrences:', scheduleForm.occurrences);

		const newSchedule = {
			...scheduleForm,
			id: `temp-${Date.now()}`, // Temporary ID for UI tracking
			customDates:
				scheduleForm.occurrences === 'custom' ? scheduleForm.customDates : [],
		};

		console.log('handleAddSchedule - newSchedule:', newSchedule);

		setPMSchedulesForm([...pmSchedulesForm, newSchedule]);
		setScheduleForm(DEFAULT_SCHEDULE_FORM);
		setIsAddingSchedule(false);
	};

	const handleRemoveSchedule = (scheduleId) => {
		setPMSchedulesForm(pmSchedulesForm.filter((s) => s.id !== scheduleId));
	};

	const handleCancelAdd = () => {
		setScheduleForm(DEFAULT_SCHEDULE_FORM);
		setIsAddingSchedule(false);
	};

	const addCustomDate = () => {
		setScheduleForm({
			...scheduleForm,
			customDates: [...scheduleForm.customDates, ''],
		});
	};

	const updateCustomDate = (index, value) => {
		const newDates = [...scheduleForm.customDates];
		newDates[index] = value;
		setScheduleForm({ ...scheduleForm, customDates: newDates });
	};

	const removeCustomDate = (index) => {
		setScheduleForm({
			...scheduleForm,
			customDates: scheduleForm.customDates.filter((_, i) => i !== index),
		});
	};

	const getFrequencyLabel = (key) => {
		return FREQUENCY_OPTIONS.find((f) => f.key === key)?.name || key;
	};

	const getCategoryName = (categoryId) => {
		return categories?.find((c) => c.id === categoryId)?.name || '';
	};

	const getSopName = (sopId) => {
		return sops?.find((s) => s.id === sopId)?.name || '';
	};

	// Set default start date from contract coverage start
	const handleStartAddingSchedule = () => {
		setScheduleForm({
			...DEFAULT_SCHEDULE_FORM,
			startDate: coverageStart
				? moment(coverageStart).format('YYYY-MM-DD')
				: '',
		});
		setIsAddingSchedule(true);
	};

	return (
		<div className="col-span-2 space-y-4">
			{/* Empty State */}
			{pmSchedulesForm.length === 0 && !isAddingSchedule && (
				<div className="text-center py-8 px-4 border-2 border-dashed border-gray-200 rounded-lg">
					<CalendarIcon className="mx-auto h-10 w-10 text-gray-400" />
					<h3 className="mt-2 text-sm font-medium text-gray-900">
						No PM schedules added yet
					</h3>
					<p className="mt-1 text-sm text-gray-500">
						Add PM schedules to automatically generate work orders during the
						contract period.
					</p>
					<div className="mt-4">
						<Button type="secondary" onClick={handleStartAddingSchedule}>
							<PlusIcon className="h-4 w-4 mr-1" />
							Add PM Schedule
						</Button>
					</div>
				</div>
			)}

			{/* Schedule List */}
			{pmSchedulesForm.length > 0 && (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h4 className="text-sm font-medium text-gray-900">
							PM Schedules ({pmSchedulesForm.length})
						</h4>
						{!isAddingSchedule && (
							<button
								type="button"
								onClick={handleStartAddingSchedule}
								className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
							>
								<PlusIcon className="h-4 w-4 mr-1" />
								Add
							</button>
						)}
					</div>

					{pmSchedulesForm.map((schedule) => (
						<div
							key={schedule.id}
							className="border border-gray-200 rounded-lg p-4 bg-white"
						>
							<div className="flex items-start justify-between">
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<h5 className="text-sm font-medium text-gray-900 truncate">
											{schedule.name}
										</h5>
										{schedule.categoryId && (
											<span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
												{getCategoryName(schedule.categoryId)}
											</span>
										)}
									</div>
									<div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
										<span className="inline-flex items-center">
											<CalendarIcon className="h-3.5 w-3.5 mr-1" />
											{getFrequencyLabel(schedule.occurrences)}
										</span>
										{schedule.startDate && (
											<span>
												Starts{' '}
												{moment(schedule.startDate).format('MMM D, YYYY')}
											</span>
										)}
										<span className="inline-flex items-center">
											<ClockIcon className="h-3.5 w-3.5 mr-1" />
											{schedule.generateDaysBefore} day lead
										</span>
									</div>
									{schedule.sopId && (
										<div className="mt-1 flex items-center text-xs text-gray-500">
											<DocumentTextIcon className="h-3.5 w-3.5 mr-1" />
											SOP: {getSopName(schedule.sopId)}
										</div>
									)}
								</div>
								<button
									type="button"
									onClick={() => handleRemoveSchedule(schedule.id)}
									className="ml-2 p-1 text-gray-400 hover:text-red-600"
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Add Schedule Form */}
			{isAddingSchedule && (
				<div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50/50">
					<h4 className="text-sm font-medium text-gray-900 mb-4">
						Add PM Schedule
					</h4>

					<div className="space-y-4">
						{/* Name */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								value={scheduleForm.name}
								onChange={(e) =>
									setScheduleForm({ ...scheduleForm, name: e.target.value })
								}
								placeholder="Enter PM schedule name"
								className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>

						{/* Frequency */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Frequency
							</label>
							<Listbox
								value={scheduleForm.occurrences}
								onChange={(val) =>
									setScheduleForm({ ...scheduleForm, occurrences: val })
								}
							>
								<div className="relative">
									<Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm">
										<span>{getFrequencyLabel(scheduleForm.occurrences)}</span>
										<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
											<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
										</span>
									</Listbox.Button>
									<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
										{FREQUENCY_OPTIONS.map((option) => (
											<Listbox.Option
												key={option.key}
												value={option.key}
												className={({ active }) =>
													classNames(
														active ? 'bg-indigo-50' : '',
														'relative cursor-pointer select-none py-2 pl-3 pr-9',
													)
												}
											>
												{({ selected }) => (
													<>
														<span>{option.name}</span>
														{selected && (
															<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																<CheckIcon className="h-5 w-5" />
															</span>
														)}
													</>
												)}
											</Listbox.Option>
										))}
									</Listbox.Options>
								</div>
							</Listbox>
						</div>

						{/* Custom Dates */}
						{scheduleForm.occurrences === 'custom' && (
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Custom Dates
								</label>
								<div className="space-y-2">
									{scheduleForm.customDates.map((date, index) => (
										<div key={index} className="flex items-center gap-2">
											<div className="flex-1">
												<DatePicker
													value={date}
													onChange={(newDate) =>
														updateCustomDate(index, newDate)
													}
													placeholder="Select date"
												/>
											</div>
											<button
												type="button"
												onClick={() => removeCustomDate(index)}
												className="p-2 text-gray-400 hover:text-red-600"
											>
												<TrashIcon className="w-4 h-4" />
											</button>
										</div>
									))}
									<button
										type="button"
										onClick={addCustomDate}
										className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
									>
										<PlusIcon className="w-4 h-4" />
										Add Date
									</button>
								</div>
								{coverageStart && coverageEnd && (
									<p className="text-xs text-gray-500 mt-1">
										Dates must be within contract coverage period (
										{moment(coverageStart).format('MMM D, YYYY')} -{' '}
										{moment(coverageEnd).format('MMM D, YYYY')})
									</p>
								)}
							</div>
						)}

						{/* Start Date & Lead Time */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Start Date
								</label>
								<DatePicker
									value={scheduleForm.startDate}
									onChange={(date) =>
										setScheduleForm({
											...scheduleForm,
											startDate: date,
										})
									}
									placeholder="Select start date"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Lead Time (days)
								</label>
								<input
									type="number"
									min="0"
									value={scheduleForm.generateDaysBefore}
									onChange={(e) =>
										setScheduleForm({
											...scheduleForm,
											generateDaysBefore: e.target.value,
										})
									}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
								/>
							</div>
						</div>

						{/* Form Actions */}
						<div className="flex justify-end gap-3 pt-2">
							<Button type="secondary" onClick={handleCancelAdd}>
								Cancel
							</Button>
							<Button
								type="primary"
								onClick={handleAddSchedule}
								disabled={!scheduleForm.name.trim()}
							>
								Add Schedule
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Add button when list has items but form is not open */}
			{pmSchedulesForm.length > 0 && !isAddingSchedule && (
				<div className="pt-2">
					<Button type="ghost" onClick={handleStartAddingSchedule}>
						<PlusIcon className="h-4 w-4 mr-1" />
						Add Another PM Schedule
					</Button>
				</div>
			)}
		</div>
	);
};

export default ContractPMSchedules;
